import { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { fetchFeaturedBooks } from "../../../app/slices/bookSlice";
import { translate } from "../../../app/utils";
import { Box, Typography, Paper, Fade, Skeleton } from "@mui/material";

const FeaturedBooks = () => {
  const {
    list: books,
    status,
    error,
  } = useSelector(({ books }) => books.featured);

  const dispatch = useDispatch();
  const navigate = useNavigate();
  const language =
    localStorage.getItem("currentLanguage")?.toUpperCase() || "EN";
  const placeholderImg = "/placeholder-book.svg";

  useEffect(() => {
    dispatch(fetchFeaturedBooks());
  }, [dispatch]);

  const BuildListBooks = () => {
    if (status === "loading") {
      return (
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
            gap: 2,
            p: 2,
          }}
        >
          {Array.from(new Array(4)).map((_, index) => (
            <Paper
              key={`skeleton-${index}`}
              elevation={1}
              sx={{
                display: "flex",
                flexDirection: "row",
                height: 170,
                bgcolor: "background.paper",
                borderRadius: 2,
                overflow: "hidden",
                border: "1px solid",
                borderColor: "divider",
              }}
            >
              <Box sx={{ width: "30%", height: "100%" }}>
                <Skeleton variant="rectangular" width="100%" height="100%" animation="wave" />
              </Box>
              <Box sx={{ width: "70%", p: 1.5, display: "flex", flexDirection: "column" }}>
                <Skeleton variant="text" width="90%" height={24} animation="wave" />
                <Skeleton variant="text" width="60%" height={24} animation="wave" />
                <Skeleton variant="rectangular" width="40%" height={20} animation="wave" sx={{ mt: 1, mb: 1, borderRadius: 1 }} />
                <Skeleton variant="text" width="100%" height={16} animation="wave" sx={{ mt: 'auto' }} />
                <Skeleton variant="text" width="80%" height={16} animation="wave" />
              </Box>
            </Paper>
          ))}
        </Box>
      );
    }

    if (status === "idle" && !error && books?.length > 0) {
      return (
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
            gap: 2,
            p: 2,
          }}
        >
          {books.map((book, index) => (
            <Fade
              in={true}
              timeout={300 + index * 100}
              key={`featured-book-${book.ID}`}
            >
              <Paper
                elevation={1}
                sx={{
                  display: "flex",
                  flexDirection: "row",
                  height: 170,
                  bgcolor: "background.paper",
                  borderRadius: 2,
                  overflow: "hidden",
                  transform: "translateY(0)",
                  transition: "all 0.2s ease !important",
                  border: "1px solid",
                  borderColor: "divider",
                  cursor: "pointer",
                  "&:hover": {
                    transform: "translateY(-4px)",
                    boxShadow: "0 12px 24px rgba(0,0,0,0.08)",
                    "& .cover-image": {
                      transform: "scale(1.05)",
                    },
                  },
                }}
                onClick={() => {
                  navigate("/books/details", { state: { book: { ...book } } });
                }}
              >
                <Box
                  sx={{
                    width: "30%",
                    height: "100%",
                    overflow: "hidden",
                    position: "relative",
                  }}
                >
                  <Box
                    className="cover-image"
                    component="img"
                    src={book.COVER_IMAGE || placeholderImg}
                    onError={(e) => {
                      e.target.onerror = null;
                      e.target.src = placeholderImg;
                    }}
                    alt={book[`TITLE_${language}`] || book.TITLE_EN}
                    sx={{
                      width: "100%",
                      height: "100%",
                      objectFit: "cover",
                      transition: "transform 0.4s ease",
                    }}
                  />
                </Box>
                <Box
                  sx={{
                    width: "70%",
                    p: 1.5,
                    display: "flex",
                    flexDirection: "column",
                  }}
                >
                  <Typography
                    variant="subtitle2"
                    fontWeight="700"
                    sx={{
                      lineHeight: 1.2,
                      mb: 0.5,
                      display: "-webkit-box",
                      WebkitLineClamp: 2,
                      WebkitBoxOrient: "vertical",
                      overflow: "hidden",
                    }}
                  >
                    {book[`TITLE_${language}`] || book.TITLE_EN}
                  </Typography>
                  <Typography
                    variant="caption"
                    fontWeight="600"
                    sx={{
                      mb: 1,
                      display: "inline-block",
                      color: "text.secondary",
                      alignSelf: "flex-start",
                    }}
                  >
                    {book.AUTHOR_NAME || "Unknown"}
                  </Typography>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{
                      display: "-webkit-box",
                      WebkitLineClamp: 3,
                      WebkitBoxOrient: "vertical",
                      overflow: "hidden",
                      fontSize: "0.8rem",
                      lineHeight: 1.4,
                      opacity: 0.8,
                    }}
                  >
                    {book[`SUMMARY_${language}`] ||
                      book.SUMMARY_EN ||
                      book[`DESCRIPTION_${language}`] ||
                      book.DESCRIPTION_EN ||
                      "No summary available."}
                  </Typography>
                </Box>
              </Paper>
            </Fade>
          ))}
        </Box>
      );
    }
    return null;
  };

  return (
    <Box sx={{ display: "flex", flexWrap: "wrap", width: "100%" }}>
      <Box
        sx={{
          width: { xs: "100%", md: "40%" },
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          p: { xs: 2, md: 4 },
        }}
      >
        <Typography 
          variant="h4" 
          fontWeight="800" 
          align="center"
          sx={{ 
            fontSize: { xs: "1.8rem", md: "2.2rem" },
            letterSpacing: 2,
            color: "text.secondary",
            textTransform: "uppercase"
          }}
        >
          {translate("featuredBooks")}
        </Typography>
      </Box>
      <Box sx={{ width: { xs: "100%", md: "60%" } }}>{BuildListBooks()}</Box>
    </Box>
  );
};

export default FeaturedBooks;
